/**
 * Confirm Dialog Component
 *
 * Reusable confirmation dialog to replace window.confirm()
 * Supports different variants (danger, warning, info) and loading state
 */

import { useState, useCallback } from 'react'
import { AlertTriangle, AlertCircle, Info, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './Dialog'
import { Button, type ButtonProps } from './Button'

// =============================================================================
// Types
// =============================================================================

type ConfirmVariant = 'danger' | 'warning' | 'info'

interface ConfirmDialogProps {
  /** Whether the dialog is open */
  open: boolean
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void
  /** Dialog title */
  title: string
  /** Dialog description/message */
  description: string
  /** Confirm button label */
  confirmLabel?: string
  /** Cancel button label */
  cancelLabel?: string
  /** Visual variant */
  variant?: ConfirmVariant
  /** Callback when confirmed */
  onConfirm: () => void | Promise<void>
  /** Whether the confirm action is loading */
  isLoading?: boolean
}

// =============================================================================
// Variant Configurations
// =============================================================================

const VARIANT_CONFIG: Record<
  ConfirmVariant,
  {
    icon: React.ComponentType<{ className?: string }>
    iconClass: string
    buttonVariant: ButtonProps['variant']
  }
> = {
  danger: {
    icon: AlertTriangle,
    iconClass: 'text-red-500',
    buttonVariant: 'destructive',
  },
  warning: {
    icon: AlertCircle,
    iconClass: 'text-yellow-500',
    buttonVariant: 'default',
  },
  info: {
    icon: Info,
    iconClass: 'text-blue-500',
    buttonVariant: 'default',
  },
}

// =============================================================================
// Component
// =============================================================================

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  variant = 'danger',
  onConfirm,
  isLoading = false,
}: ConfirmDialogProps) {
  const config = VARIANT_CONFIG[variant]
  const Icon = config.icon

  const handleConfirm = async () => {
    await onConfirm()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={`h-5 w-5 ${config.iconClass}`} />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <DialogFooter className="mt-6">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={config.buttonVariant}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Procesando...
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Hook for easier usage
// =============================================================================

interface UseConfirmDialogOptions {
  title: string
  description: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: ConfirmVariant
  onConfirm: () => void | Promise<void>
}

export function useConfirmDialog() {
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [options, setOptions] = useState<UseConfirmDialogOptions | null>(null)

  const confirm = useCallback((opts: UseConfirmDialogOptions) => {
    setOptions(opts)
    setIsOpen(true)
  }, [])

  const handleConfirm = useCallback(async () => {
    if (!options) return
    setIsLoading(true)
    try {
      await options.onConfirm()
      setIsOpen(false)
    } finally {
      setIsLoading(false)
    }
  }, [options])

  const dialogProps = {
    open: isOpen,
    onOpenChange: setIsOpen,
    title: options?.title ?? '',
    description: options?.description ?? '',
    confirmLabel: options?.confirmLabel,
    cancelLabel: options?.cancelLabel,
    variant: options?.variant,
    onConfirm: handleConfirm,
    isLoading,
  }

  return {
    confirm,
    dialogProps,
    ConfirmDialog,
  }
}
